<script lang="ts" setup>
import type { FieldFilter, PrimitiveFieldPath } from '#layers/data-model/public';
import type { FilterSchemaCollection } from './utils/filter-schema';
import type { RelatedItemOperator } from './utils/related-item-match';
import { useEmitAsProps } from 'reka-ui';
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { FilterOperator, useFieldTypeTemplate } from '#layers/data-model/public';
import { getDefaultOperatorForField } from '#layers/filters/composables/use-filter-operator';
import { useRelatedItemClauseEditor } from '#layers/filters/composables/use-related-item-clause-editor';
import { useRelatedItemFilter } from '#layers/filters/composables/use-related-item-filter';
import { useRelatedItemMatch } from '#layers/filters/composables/use-related-item-match';
import { resolveFilterComponent } from '#layers/ui/public';
import FilterRelatedItemInput from './FilterRelatedItemInput.vue';
import { buildRelationPathSegments, resolveFieldPathSegments, serializeSegmentedFilter } from './utils/filter-path';
import { injectFilterSchemaContext } from './utils/filter-schema-context';
import { seedOperatorState } from './utils/filter-ui-operator';
import { useFilterConfigResolver } from './utils/use-filter-config-resolver';

const { collection, disabled = false, readonly = false, hideGroupActions = false } = defineProps<{
	collection: FilterSchemaCollection;
	disabled?: boolean;
	readonly?: boolean;
	hideGroupActions?: boolean;
}>();

const emit = defineEmits<{
	remove: [];
	duplicate: [];
	turnIntoGroup: [];
}>();

const filter = defineModel<FieldFilter>({ required: true });

const { t } = useI18n();
const schema = injectFilterSchemaContext();
const { parseRelatedItemClause, serializeRelatedItemClause } = useRelatedItemMatch();
const { buildRelatedItemCondition } = useRelatedItemFilter();
const { supportedOperatorsFor, defaultOperatorFor } = useFilterConfigResolver();
const actionEmits = useEmitAsProps(emit);

const clause = computed(() => parseRelatedItemClause(filter.value, collection));

const terminalRelationField = computed(() => {
	const id = clause.value?.displayFieldPath.at(-1);
	return id ? schema.findSingleRelationFieldById(id) ?? null : null;
});
const { template: terminalTypeTemplate } = useFieldTypeTemplate(() => terminalRelationField.value);
const relationMatchPicker = computed(() => terminalTypeTemplate.value?.type === 'relation' ? resolveFilterComponent(terminalTypeTemplate.value.filter?.picker) : undefined);
const relationMatchPreview = computed(() => terminalTypeTemplate.value?.type === 'relation' ? resolveFilterComponent(terminalTypeTemplate.value.filter?.preview) : undefined);
const relationMatchPreviewFields = computed(() => terminalTypeTemplate.value?.type === 'relation' ? terminalTypeTemplate.value.filter?.previewFields : undefined);

const autoOpenToken = ref(0);

// Bumping the token drives the inline picker (popover) to open. The guard keeps the drawer path
// (no inline picker) from reopening its drawer, so this is safe to call unconditionally.
function requestAutoOpen() {
	if (relationMatchPicker.value) autoOpenToken.value++;
}

// The operator/keys editing state machine (writable operator, single↔list swap, polarity preservation).
const { writableOperator, onKeysUpdate } = useRelatedItemClauseEditor(clause, {
	serialize: serializeRelatedItemClause,
	onChange: (value) => {
		filter.value = value;
	},
});

// The relation hops rendered as the (interactive) field-path chip — the display path, so an M2M's
// junction hop stays hidden. Each hop keeps its real cardinality so re-targeting preserves quantifiers.
const segments = computed(() => buildRelationPathSegments(clause.value?.displayFieldPath ?? [], (id) => schema.findSingleRelationFieldById(id)));

// The chip renders the junction-hidden `segments`, but editing a segment must rebuild the *real* path
// (junction hops included), or an M2M's hidden junction hop would be dropped from the emitted filter.
const realSegments = computed(() => buildRelationPathSegments(clause.value?.relationFieldPath ?? [], (id) => schema.findSingleRelationFieldById(id)));

// Maps a display-segment index (what `FilterFieldPath` emits) to its position in the real path.
function realIndexForDisplayIndex(displayIndex: number): number {
	const current = clause.value;
	if (!current) return displayIndex;

	return current.displayFieldPathRealIndices[displayIndex] ?? current.relationFieldPath.length;
}

// Both to-one and to-many/M2M hops offer the four identity operators (a to-many's polarity maps to
// `_some`/`_none` on serialize). All read as "is / is not / is one of / isn't one of".
const OPERATORS: RelatedItemOperator[] = [FilterOperator.EQ, FilterOperator.NEQ, FilterOperator.IN, FilterOperator.NIN];

const operatorOptions = computed<{ label: string; value: RelatedItemOperator }[]>(() =>
	OPERATORS.map((value) => ({ label: t(`filters.operators.${value}`), value })),
);

onMounted(() => {
	if (clause.value && clause.value.keys.length === 0) requestAutoOpen();
});

// Selecting a primitive field in the chip's menu converts this row back into a plain filter. Rebuild from
// the real segments (junction hops kept) so serializing an M2M path doesn't drop the hidden junction hop.
function onFieldPathSelect(displayIndex: number, fieldPath: PrimitiveFieldPath) {
	const resolved = resolveFieldPathSegments(realSegments.value, realIndexForDisplayIndex(displayIndex), fieldPath, collection, schema);
	if (!resolved) return;

	const { terminalPrimitiveField: field } = resolved;
	const seedOperator = field ? getDefaultOperatorForField(field, supportedOperatorsFor(field), defaultOperatorFor(field)) : undefined;
	const seed = seedOperatorState(seedOperator);

	filter.value = serializeSegmentedFilter({ segments: resolved.segments, ...seed }, schema);
}

// Selecting a relation in the chip re-targets to a different relation, opening the drawer to pick item(s).
// Keep the real hops before the edited segment (junction hops included) and replace the rest.
async function onRetargetRelation(displayIndex: number, relationFieldPath: string[]) {
	const current = clause.value;
	if (!current) return;

	const keptRelationPath = current.relationFieldPath.slice(0, realIndexForDisplayIndex(displayIndex));
	const condition = await buildRelatedItemCondition([...keptRelationPath, ...relationFieldPath]);

	if (condition) {
		filter.value = condition;
		// Retargeting onto an inline-picker relation seeds an empty match; open its picker.
		requestAutoOpen();
	}
}
</script>

<template>
	<FilterHandleGroup v-if="clause">
		<FilterFieldPath
			:segments
			:disabled
			enable-relation-select
			:show-quantifiers="false"
			resolve-junction-targets
			@update-from-segment="onFieldPathSelect"
			@select-relation="onRetargetRelation"
		/>

		<DSelectMenu
			v-model="writableOperator"
			:items="operatorOptions"
			value-key="value"
			variant="ghost"
			trailing-icon=""
			class="rounded-none"
			:ui="{
				base: 'px-2',
				content: 'min-w-fit',
				value: 'text-xs',
			}"
			:disabled
		/>

		<FilterRelatedItemInput
			:operator="clause.operator"
			:keys="clause.keys"
			:opposite-collection="clause.oppositeCollection"
			:primary-key-fields="clause.primaryKeyFields"
			:relation-match-picker
			:relation-match-preview
			:relation-match-preview-fields
			allow-current-user
			:auto-open="autoOpenToken"
			:disabled
			:readonly
			@update:keys="onKeysUpdate"
		/>

		<FilterActions v-if="hideGroupActions && !readonly" v-bind="actionEmits" can-turn-into-group :disabled />
		<FilterRemove v-else-if="!readonly" v-bind="actionEmits" :disabled />
	</FilterHandleGroup>
</template>
